import { useEffect, useMemo, useState, useRef } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { ColDef, GridReadyEvent, RowClickedEvent } from "ag-grid-community";
import { DataGrid } from "./Controls/DataGrid";
import { Plus, Map } from "react-bootstrap-icons";
import { PageBar, ToolButton } from "./Controls/PageBar";
import { useAtom } from "jotai";
import { createApiService } from "./shared/apiConfig";
import { FacilityApi, ServerTransferFacilityStats } from "./shared/api/axios-client";
import { showMoney, cents } from "./Data/Money";
import { CSSTransition } from "react-transition-group";
import { navigationService } from "./services/NavigationService";

export function FacilitiesListPage() {
    const nav = useNavigate();
    
    const [facilitiesData, setFacilitiesData] = useState<ServerTransferFacilityStats[]>([]);
    const [showContent, setShowContent] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);
    const nodeRef = useRef(null);
    const facilityService = createApiService(FacilityApi);

    useEffect(() => {
        const handleResize = () => {
            setIsSmallScreen(window.innerWidth < 768);
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const columns: ColDef<ServerTransferFacilityStats>[] = [
                { 
            field: "facilityName", 
            headerName: "Facility Name",
            headerTooltip: "Facility Name",
            flex: 2,
            cellRenderer: (params: any) => {
                const data = params.data as ServerTransferFacilityStats;
                if (!data) return '';
                
                const addressParts = [
                    data.streetAddress,
                    data.city,
                    data.state,
                    data.zip
                ].filter(part => part != null && part !== '');
                
                const addressText = addressParts.join(', ');
                
                return (
                    <div style={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        justifyContent: 'center',
                        padding: '4px 0'
                    }}>
                        <div style={{ fontWeight: 500, marginBottom: '2px', lineHeight: '1.2' }}>
                            {data.facilityName || ''}
                        </div>
                        {addressParts.length > 0 && (
                            <div style={{ fontSize: '0.85em', color: '#6c757d', lineHeight: '1.2' }}>
                                {addressText}
                            </div>
                        )}
                    </div>
                );
            },
            getQuickFilterText: (params: any) => {
                const data = params.data as ServerTransferFacilityStats;
                if (!data) return '';
                
                const addressParts = [
                    data.streetAddress,
                    data.city,
                    data.state,
                    data.zip
                ].filter(part => part != null && part !== '');
                
                const addressText = addressParts.join(' ');
                
                return `${data.facilityName || ''} ${addressText}`.trim();
            }
        },
        { 
            field: "totalMonthlyParkerCount", 
            headerName: "Monthly Parkers",
            headerTooltip: "Monthly Parkers",
            type: "rightAligned",
            flex: 1,
            hide: isSmallScreen,
            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }
        },
        { 
            field: "pendingParkers", 
            headerName: "New Applications",
            headerTooltip: "New Applications",
            type: "rightAligned",
            flex: 1,
            hide: isSmallScreen,
            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }
        },
        { 
            field: "activeReservations", 
            headerName: "Active Transient",
            headerTooltip: "Active Transient",
            type: "rightAligned",
            flex: 1,
            hide: isSmallScreen,
            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }
        },
        { 
            field: "upcomingReservations", 
            headerName: "Upcoming Transient",
            headerTooltip: "Upcoming Transient",
            type: "rightAligned",
            flex: 1,
            hide: isSmallScreen,
            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }
        },
        { 
            field: "monthlyRecurringRevenue", 
            headerName: "Monthly Revenue",
            headerTooltip: "Monthly Recurring Revenue",
            type: "rightAligned",
            flex: 1,
            hide: isSmallScreen,
            valueFormatter: (params) => params.value !== null && params.value !== undefined ? showMoney(cents(params.value)) : "",
            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }
        },
        { 
            field: "currentMonthTransientRevenue", 
            headerName: "Transient Revenue",
            headerTooltip: "Current Month Transient Revenue",
            type: "rightAligned",
            flex: 1,
            hide: isSmallScreen,
            valueFormatter: (params) => params.value !== null && params.value !== undefined ? showMoney(cents(params.value)) : "",
            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }
        },
        { 
            field: "totalUnpaidBalance", 
            headerName: "Unpaid Balance",
            headerTooltip: "Total Unpaid Balance",
            type: "rightAligned",
            width: 150,
            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end' },
            cellRenderer: (params: any) => {
                if (params.value === null || params.value === undefined) {
                    return <></>;
                }
                const balance = cents(params.value);
                const isPositive = params.value > 0;
                const tokenClass = isPositive ? "bg-danger bg-opacity-10" : "bg-success bg-opacity-10";
                const textClass = isPositive ? "text-danger" : "text-success";
                
                return (
                    <div className={`d-flex align-items-center justify-content-end w-100`}>
                        <span className={`badge ${tokenClass} ${textClass} px-2 py-1 fs-6`}>
                            {showMoney(balance)}
                        </span>
                    </div>
                );
            }
        }
    ];

    const onGridReady = (params: GridReadyEvent) => {
        params.api.sizeColumnsToFit();
    };

    const onRowClicked = (event: RowClickedEvent<ServerTransferFacilityStats>) => {
        if (event.event?.defaultPrevented) return;
        
        const facilityId = event.data?.facilityId;
        if (facilityId) {
            navigationService.navigateToFacilityFromGrid(nav, facilityId);
        }
    };


    const fetchFacilitiesData = async () => {
        try {
            // Small delay to ensure mask system is ready
            await new Promise(resolve => setTimeout(resolve, 10));
            console.log("Fetching facilities data");

            const response = await facilityService.getFacilitiesStats();
            setFacilitiesData(response.data || []);
        } catch (error) {
            console.error("Error loading facilities data:", error);
        } finally {
            console.log("Finished loading facilities data");
            setShowContent(true);
        }
    };


    
    useEffect(() => {
        // Record that user is on grid view
        navigationService.setLastFacilitiesView('grid');
        fetchFacilitiesData();
    }, []);

    return (
        <CSSTransition
            in={showContent}
            timeout={300}
            classNames="page-slide"
            unmountOnExit
            nodeRef={nodeRef}
        >
            <Container fluid className="h-100" ref={nodeRef}>
                <Row className="h-100">
                    <Col className="h-100" style={{ 
                        display: "grid", 
                        gridTemplateColumns: "1fr", 
                        gridTemplateRows: "max-content 1fr 5px" 
                    }}>
                        <PageBar title="Facilities">
                            <ToolButton onClick={() => nav("/operator/facility/create")}>
                                <Plus color="maroon" /> Add Facility
                            </ToolButton>
                            <div className="flex-grow-1"></div>
                            {!isSmallScreen && (
                                <ToolButton onClick={() => navigationService.navigateToFacilitiesMap(nav)}>
                                    <Map color="maroon" /> Map View
                                </ToolButton>
                            )}
                        </PageBar>
                        <div style={{ height: "100%", width: "100%" }}>
                            <DataGrid
                                rowData={facilitiesData}
                                columnDefs={columns}
                                onGridReady={onGridReady}
                                onRowClicked={onRowClicked}
                                pagination={true}
                                paginationPageSize={25}
                                paginationPageSizeSelector={[10, 25, 50, 100]}
                                defaultColDef={{
                                    sortable: true,
                                    filter: true,
                                    resizable: true,
                                    cellStyle: {
                                        display: 'flex',
                                        alignItems: 'center'
                                    }
                                }}
                                rowStyle={{ cursor: 'pointer' }}
                                rowHeight={60}
                                getRowHeight={() => 60}
                                tooltipShowDelay={200}
                            />
                        </div>
                    </Col>
                </Row>
            </Container>
        </CSSTransition>
    );
} 